import { Button, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { CoreButton } from "@verity/components/core";
import { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import apiClient from "../../../../helpers/api-client";
import { intIdToUuid } from "../../../../helpers/uuid";
import i18n from "../../../../i18n";
import { PortalControllerContext } from "../../../context";
import styles from "./InterviewPage.module.css";
import InterviewSkeletonWrapper from "./InterviewSkeletonWrapper";
import InterviewPageLayout from "./layouts/InterviewPageLayout";
import { CaretLeftIcon, Recording } from "./pages/InprogressPage/assets";
import InprogressPage from "./pages/InprogressPage/InprogressPage";
import WaitingPage from "./pages/WaitingPage/WaitingPage";
import WaitingSkeleton from "./pages/WaitingPage/WaitingSkeleton";
import { ProctoringOptionsType, SessionType } from "./types";

export default function InterviewPage() {
	const { portalContext } = useContext(PortalControllerContext);
	const { lmsProvidedCourseId, instituteUuid, courseUuid } = portalContext;
	const navigate = useNavigate();
	const { id } = useParams();
	const params = new URLSearchParams(window.location.search);
	const quizId = params.get("quizId");

	const {
		data: proctoringConfigurationOptions,
		error: configError,
		isLoading: configLoading,
	} = useQuery({
		queryKey: ["proctoring-config", quizId, lmsProvidedCourseId],
		enabled: !!quizId && !!lmsProvidedCourseId,
		queryFn: async (): Promise<ProctoringOptionsType> => {
			const result = await apiClient.proctoringConfiguration.get({
				quizId: quizId!,
				courseId: lmsProvidedCourseId!,
			});

			//Ensure exists
			if (!result) {
				throw new Error("Missing proctoring configuration");
			}

			//Ensure candidateInterviewOptions exists
			if (!result.candidateInterviewOptions) {
				throw new Error("Missing candidate options");
			}

			//Do this to avoid type script error
			const { candidateInterviewOptions, ...rest } = result;
			return {
				...rest,
				candidateInterviewOptions,
			};
		},
	});

	const {
		data: session,
		error: sessionError,
		isLoading: sessionLoading,
	} = useQuery({
		queryKey: ["live-session", instituteUuid, courseUuid, id],
		enabled: !!instituteUuid && !!courseUuid && !!id,
		queryFn: async (): Promise<SessionType> => {
			const result = await apiClient.liveClient.getLiveSessionsWithUsers({
				instituteUuid: instituteUuid!,
				courseUuid: courseUuid!,
				assessmentUuid: intIdToUuid(Number(id)),
			});

			if (!result.results[0]) {
				throw new Error("Missing live session");
			}

			return result.results[0];
		},
	});

	//Look for candidate joined and candidate left events instead?
	const {
		data: status,
		error: statusError,
		isLoading: statusLoading,
	} = useQuery({
		queryKey: ["live-session-status", session?.id],
		enabled: !!session?.id,
		queryFn: async (): Promise<boolean> => {
			const result = await apiClient.liveClient.getLiveSessionLiveState(session!.id);
			if (!result?.userSessionIds) return false;
			return result.userSessionIds.length >= 0;
		},
		refetchInterval: (data) => {
			if (data) return false; // stop polling
			return 10000; // poll every 10 seconds
		},
	});

	if (configLoading || sessionLoading || statusLoading)
		return (
			<InterviewSkeletonWrapper>
				<WaitingSkeleton />
			</InterviewSkeletonWrapper>
		);

	if (configError || sessionError || statusError)
		throw new Error(configError?.message || sessionError?.message || statusError?.message);

	if (!proctoringConfigurationOptions || !session) return null;

	const { title, candidateName } = proctoringConfigurationOptions.candidateInterviewOptions;

	return (
		<InterviewPageLayout>
			<Stack direction="row" justifyContent="space-between">
				<Stack direction="row" spacing="15px" alignItems="center">
					<Button startIcon={<CaretLeftIcon />} onClick={() => navigate("/interviews")}>
						<Typography className={styles.backButton}>
							{i18n.t("candidate.instructorInterviewPage.buttons.back")}
						</Typography>
					</Button>
					<Typography className={styles.header}>
						{i18n.t("candidate.instructorInterviewPage.title", { title, candidateName })}
					</Typography>
				</Stack>
				<Stack direction="row" gap="15px">
					<Stack direction="row" spacing="5px" alignItems="center">
						<img src={Recording} />
						<Typography className={styles.status}>{i18n.t("candidate.instructorInterviewPage.status")}</Typography>
					</Stack>
					{status && (
						<CoreButton
							color="cancel"
							onClick={() => navigate(`/interviews/${session.id}/completed`, { state: { id, quizId } })}
						>
							{i18n.t("candidate.instructorInterviewPage.buttons.end")}
						</CoreButton>
					)}
				</Stack>
			</Stack>

			{status ? (
				<InprogressPage proctoringOptions={proctoringConfigurationOptions} sessionInfo={session} />
			) : (
				<WaitingPage candidateOptions={proctoringConfigurationOptions.candidateInterviewOptions} />
			)}
		</InterviewPageLayout>
	);
}
